import { Link, useLocation } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { ShoppingBag, Menu, X, Search, ArrowUp, Instagram, Youtube, Facebook } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useSettings } from "@/context/SettingsContext";
import { api } from "@/lib/api";
import { TRANSPARENT_LOGO, BRAND_GREEN } from "@/lib/brand";
import { toast } from "sonner";

const LOGO = TRANSPARENT_LOGO;

// Small icon set for social links (Instagram/YouTube/Facebook use lucide; TikTok + Pinterest as inline SVG)
const TiktokIcon = (p) => (<svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18" {...p}><path d="M20 8.5a6.5 6.5 0 0 1-4.2-1.5v7.4a5.6 5.6 0 1 1-5.6-5.6c.3 0 .6 0 .8.1v2.5a3.1 3.1 0 1 0 2.2 3V2h2.5a4 4 0 0 0 4.3 4z"/></svg>);
const PinterestIcon = (p) => (<svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18" {...p}><path d="M12 2a10 10 0 0 0-3.6 19.3c-.1-.8-.2-2 0-2.9l1.2-5s-.3-.6-.3-1.5c0-1.4.8-2.4 1.8-2.4.9 0 1.3.6 1.3 1.4 0 .9-.6 2.1-.9 3.3-.2 1 .5 1.9 1.5 1.9 1.8 0 3.2-1.9 3.2-4.7 0-2.4-1.8-4.2-4.3-4.2A4.4 4.4 0 0 0 6.4 12c0 .9.3 1.9.7 2.4.1.1.1.2.1.3l-.3 1.3c-.1.3-.2.3-.5.2-1.5-.7-2.4-2.8-2.4-4.5 0-3.7 2.7-7.1 7.7-7.1 4.1 0 7.2 2.9 7.2 6.7 0 4-2.5 7.3-6 7.3-1.2 0-2.3-.6-2.6-1.3l-.7 2.8c-.3 1-1 2.3-1.5 3.1A10 10 0 1 0 12 2z"/></svg>);

export default function Layout({ children }) {
  const { count, setOpen } = useCart();
  const { settings } = useSettings();
  const [menuOpen, setMenuOpen] = useState(false);
  const [emailVal, setEmailVal] = useState("");
  const [subLoading, setSubLoading] = useState(false);
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith("/admin");

  const archiveLabel = settings?.archive?.nav_label || "Found a New Home";
  const announcement = settings?.announcement || { text: "", active: true };
  const social = settings?.social || {};
  const footer = settings?.footer || {};
  const email = settings?.email_signup || { active: true };
  const promo = settings?.promo_banner || { active: false };

  // Dynamic footer pages (owner-managed via /admin/pages)
  const [footerPages, setFooterPages] = useState([]);
  useEffect(() => {
    api.get(`/pages?_=${Date.now()}`).then((r) => setFooterPages(r.data || [])).catch(() => setFooterPages([]));
  }, [pathname]);

  const navItems = [
    { to: "/shop", label: "Shop All", testid: "nav-shop" },
    { to: "/collections", label: "Collections", testid: "nav-collections" },
    { to: "/archive", label: archiveLabel, testid: "nav-archive" },
    { to: "/about", label: "About", testid: "nav-about" },
  ];

  // Accessible mobile menu: focus close button on open + close on Escape
  const closeBtnRef = useRef(null);
  useEffect(() => {
    if (!menuOpen) return;
    closeBtnRef.current?.focus();
    const onKey = (e) => { if (e.key === "Escape") setMenuOpen(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [menuOpen]);

  // Back to Top — respects reduced-motion preference
  const scrollToTop = () => {
    const reduce = typeof window !== "undefined" && window.matchMedia
      && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    window.scrollTo({ top: 0, behavior: reduce ? "auto" : "smooth" });
  };

  const subscribe = async (e) => {
    e.preventDefault();
    if (!emailVal) return;
    setSubLoading(true);
    try {
      await api.post("/newsletter/subscribe", { email: emailVal, source: "footer", consent: true });
      toast.success(email.success_message || "You're on the ledger.");
      setEmailVal("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Please enter a valid email");
    } finally { setSubLoading(false); }
  };

  if (isAdmin) return <>{children}</>;

  const socials = [
    { url: social.instagram_url, Icon: Instagram, name: "Instagram" },
    { url: social.tiktok_url, Icon: TiktokIcon, name: "TikTok" },
    { url: social.pinterest_url, Icon: PinterestIcon, name: "Pinterest" },
    { url: social.facebook_url, Icon: Facebook, name: "Facebook" },
    { url: social.youtube_url, Icon: Youtube, name: "YouTube" },
  ].filter((s) => s.url && /^https?:\/\//i.test(s.url));

  return (
    <div className="min-h-screen flex flex-col">
      {/* Announcement bar — wraps on mobile, single line on desktop */}
      {announcement.active && announcement.text && (
        <div className="bg-oxblood text-parchment text-[10px] sm:text-[11px] font-caption py-2 px-4 text-center">
          <div className="max-w-4xl mx-auto leading-snug sm:whitespace-nowrap sm:overflow-hidden sm:text-ellipsis">{announcement.text}</div>
        </div>
      )}

      {/* Promotional banner (owner controlled) */}
      {promo.active && promo.message && (
        <div data-testid="promo-banner" className="bg-gold text-forest text-[12px] font-caption py-2 text-center">
          {promo.message}
          {promo.cta_text && promo.cta_link && (
            <Link to={promo.cta_link} data-testid="promo-cta" className="ml-3 underline font-bold">
              {promo.cta_text}
            </Link>
          )}
        </div>
      )}

      {/* Header — brand-green so the transparent logo sits on the correct color. Sticky on all sizes; banners above scroll away. */}
      <header className="sticky top-0 z-40 backdrop-blur-md border-b border-brass-soft" style={{ background: "rgba(6,28,15,0.94)" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4 flex items-center justify-between gap-4">
          {/* Logo — far left on every width */}
          <Link to="/" data-testid="nav-home-logo" aria-label="Golden Moth Market — Home" className="flex items-center shrink-0">
            <img src={LOGO} alt="Golden Moth Market" className="h-12 sm:h-16 w-auto object-contain" />
          </Link>

          {/* Desktop primary navigation — shown when there is room (xl+) */}
          <nav aria-label="Primary" className="hidden xl:flex items-center gap-7 flex-1 justify-center">
            {navItems.map((n) => (
              <Link key={n.to} to={n.to} data-testid={n.testid}
                className="font-caption text-[11px] text-parchment/80 hover:text-gold transition-colors whitespace-nowrap">
                {n.label}
              </Link>
            ))}
            <Link to="/admin/login" data-testid="nav-ledger"
              className="font-caption text-[11px] text-parchment/80 hover:text-gold transition-colors whitespace-nowrap">
              {"Proprietor's Ledger"}
            </Link>
          </nav>

          {/* Right-side controls */}
          <div className="flex items-center gap-1 sm:gap-2">
            {/* Desktop-only Search + Cart icons */}
            <Link to="/shop" data-testid="header-search-btn" className="hidden xl:inline-flex text-parchment/80 hover:text-gold p-2" aria-label="Search">
              <Search size={20} />
            </Link>
            <button data-testid="cart-toggle-btn" onClick={() => setOpen(true)} aria-label="Cart"
              className="hidden xl:inline-flex relative text-parchment/90 hover:text-gold p-2">
              <ShoppingBag size={20} />
              {count > 0 && (
                <span data-testid="cart-count-badge" className="absolute -top-0.5 -right-0.5 bg-gold text-[color:var(--forest)] text-[10px] font-caption font-bold rounded-full h-5 min-w-5 px-1 flex items-center justify-center">
                  {count}
                </span>
              )}
            </button>

            {/* Mobile hamburger — far right, hidden on desktop */}
            <button data-testid="mobile-menu-btn" className="xl:hidden text-gold p-2 -mr-2"
              onClick={() => setMenuOpen(true)} aria-label="Open menu"
              aria-expanded={menuOpen} aria-controls="mobile-menu">
              <Menu size={24} />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      {menuOpen && (
        <div id="mobile-menu" role="dialog" aria-modal="true" aria-label="Menu"
          data-testid="mobile-menu"
          className="fixed inset-0 z-50 xl:hidden overflow-y-auto" style={{ background: "rgba(6,28,15,0.97)" }}>
          <div className="flex justify-end p-4">
            <button data-testid="mobile-menu-close" ref={closeBtnRef} onClick={() => setMenuOpen(false)} className="text-gold p-2" aria-label="Close menu">
              <X size={24} />
            </button>
          </div>
          <nav aria-label="Mobile" className="flex flex-col items-center gap-6 pb-16">
            <img src={LOGO} alt="Golden Moth Market" className="h-24 w-auto" />
            {navItems.map((n) => (
              <Link key={n.to} to={n.to} onClick={() => setMenuOpen(false)}
                data-testid={`mobile-${n.testid}`}
                className="font-display text-2xl text-gold py-1">{n.label}</Link>
            ))}
            <Link to="/admin/login" onClick={() => setMenuOpen(false)}
              data-testid="mobile-nav-ledger"
              className="font-display text-2xl text-gold py-1">{"Proprietor's Ledger"}</Link>

            {/* Search + Cart live inside the menu on mobile */}
            <div className="flex items-center gap-8 mt-4 pt-6 border-t border-brass-soft w-48 justify-center">
              <Link to="/shop" onClick={() => setMenuOpen(false)} data-testid="mobile-search-btn"
                aria-label="Search" className="flex flex-col items-center gap-1 text-parchment/85 hover:text-gold p-2">
                <Search size={22} />
                <span className="font-caption text-[10px]">Search</span>
              </Link>
              <button onClick={() => { setMenuOpen(false); setOpen(true); }} data-testid="mobile-cart-btn"
                aria-label="Shopping bag" className="relative flex flex-col items-center gap-1 text-parchment/90 hover:text-gold p-2">
                <ShoppingBag size={22} />
                <span className="font-caption text-[10px]">Bag{count > 0 ? ` (${count})` : ""}</span>
              </button>
            </div>
          </nav>
        </div>
      )}

      <main className="flex-1">{children}</main>

      {/* Footer */}
      <footer className="mt-24 border-t border-brass-soft" style={{ background: BRAND_GREEN }}>
        <div className="max-w-7xl mx-auto px-6 py-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
          <div>
            <img src={LOGO} alt="Golden Moth Market" className="h-20 w-auto mb-4" />
            <p className="text-parchment/70 text-sm italic">
              {footer.business_description || "Beautiful things find their way back."}
            </p>
            {socials.length > 0 && (
              <div className="flex gap-3 mt-4" data-testid="footer-social">
                {socials.map(({ url, Icon, name }) => (
                  <a key={name} href={url} target="_blank" rel="noopener noreferrer"
                    aria-label={name}
                    data-testid={`social-${name.toLowerCase()}`}
                    className="text-parchment/70 hover:text-gold p-1.5 border border-brass-soft">
                    <Icon size={16} />
                  </a>
                ))}
              </div>
            )}
          </div>
          <div>
            <h4 className="font-caption text-xs text-gold mb-4">The Market</h4>
            <ul className="space-y-2 text-parchment/70 text-sm">
              <li><Link to="/shop" className="hover:text-gold">Shop All</Link></li>
              <li><Link to="/collections" className="hover:text-gold">Collections</Link></li>
              <li><Link to="/archive" className="hover:text-gold">{archiveLabel}</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="font-caption text-xs text-gold mb-4">Proprietor</h4>
            <ul className="space-y-2 text-parchment/70 text-sm" data-testid="footer-pages">
              <li><Link to="/about" className="hover:text-gold">About</Link></li>
              {footerPages.map((p) => (
                <li key={p.id}>
                  <Link to={`/page/${p.slug}`} data-testid={`footer-page-${p.slug}`} className="hover:text-gold">
                    {p.footer_label || p.title}
                  </Link>
                </li>
              ))}
              <li><Link to="/admin/login" className="hover:text-gold">Proprietor's Ledger</Link></li>
            </ul>
          </div>
          {email.active !== false && (
            <div>
              <h4 className="font-caption text-xs text-gold mb-4">{email.heading || "Almanac"}</h4>
              <p className="text-parchment/70 text-sm mb-3">{email.description || "Word of new finds, sent by candlelight."}</p>
              <form onSubmit={subscribe} className="flex gap-2" data-testid="newsletter-form">
                <input data-testid="newsletter-email" required type="email" placeholder="your@email"
                  value={emailVal} onChange={(e) => setEmailVal(e.target.value)}
                  className="input-vintage flex-1 text-sm py-2" />
                <button data-testid="newsletter-submit" type="submit" disabled={subLoading}
                  className="btn-outline text-xs py-2 px-3">{email.button_text || "Join"}</button>
              </form>
              {email.consent_text && (<p className="text-parchment/40 text-[10px] mt-2">{email.consent_text}</p>)}
            </div>
          )}
        </div>
        <div className="border-t border-brass-soft py-6 text-center">
          <button data-testid="back-to-top" onClick={scrollToTop}
            className="font-caption text-[11px] tracking-[0.15em] uppercase text-brass hover:text-gold transition-colors inline-flex items-center gap-1.5 mb-3">
            Back to Top <ArrowUp size={12} />
          </button>
          <div className="text-parchment/50 text-xs font-caption">
            {footer.supporting_line || `© ${new Date().getFullYear()} Golden Moth Market · Est. under the crescent`}
          </div>
        </div>
      </footer>
    </div>
  );
}
